/**
 * Enhanced presentation (docs/spec/04 §3): one-pixel sparks in the virtual pixel grid, and the
 * screen's shake. Purely visual; its randomness is its own and never touches the game.
 */

import type { Colour } from '../font';
import type { Screen } from '../screen';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  colour: Colour;
  size: number;
  gravity: number;
}

const MAX = 700;

export class Particles {
  private list: Particle[] = [];
  private seed = 1;
  /** Ticks of shake left, and its strength in pixels. */
  private shakeLeft = 0;
  private shakeFor = 1;
  private shakePower = 0;

  rand(): number {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }

  clear(): void {
    this.list = [];
    this.shakeLeft = 0;
  }

  /** Sparks flying out from a point. Speeds are pixels a tick, lives are ticks. */
  burst(cx: number, cy: number, n: number, colour: Colour, speed: number, life: number, gravity = 0, size = 1): void {
    for (let i = 0; i < n && this.list.length < MAX; i++) {
      const a = this.rand() * Math.PI * 2;
      const v = speed * (0.3 + this.rand() * 0.7);
      const max = life * (0.5 + this.rand() * 0.7);
      this.list.push({ x: cx, y: cy, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: max, max, colour, size, gravity });
    }
  }

  /** One spark with a given velocity (exhaust, glitter). */
  spark(x: number, y: number, vx: number, vy: number, colour: Colour, life: number): void {
    if (this.list.length < MAX) this.list.push({ x, y, vx, vy, life, max: life, colour, size: 1, gravity: 0 });
  }

  shake(power: number, ticks: number): void {
    if (power < this.shakePower && this.shakeLeft > 0) return;
    this.shakePower = power;
    this.shakeLeft = ticks;
    this.shakeFor = ticks;
  }

  /** Move on by one tick. */
  update(): void {
    if (this.shakeLeft > 0) this.shakeLeft--;
    const keep: Particle[] = [];
    for (const p of this.list) {
      if (--p.life <= 0) continue;
      p.vy += p.gravity;
      p.x += p.vx;
      p.y += p.vy;
      keep.push(p);
    }
    this.list = keep;
  }

  /** The shake's offset this frame, in whole pixels. */
  offset(): { x: number; y: number } {
    if (this.shakeLeft <= 0) return { x: 0, y: 0 };
    const p = Math.ceil(this.shakePower * (this.shakeLeft / this.shakeFor));
    return { x: Math.round((this.rand() * 2 - 1) * p), y: Math.round((this.rand() * 2 - 1) * p) };
  }

  draw(screen: Screen): void {
    for (const p of this.list) {
      // Dying sparks flicker out rather than fade: the palette stays the arcade's.
      if (p.life < p.max * 0.35 && (Math.floor(p.life / 2) & 1) === 1) continue;
      screen.fill(p.x, p.y, p.size, p.size, p.colour);
    }
  }
}
